import React, { useCallback, useEffect, useState } from "react";
import Head from "src/layout/head/Head";
import {
  Block,
  Icon,
} from "src/components/Component";
import Skeleton from 'react-loading-skeleton'
import { useSelector } from 'react-redux';

import 'react-loading-skeleton/dist/skeleton.css'
import { Auth } from "aws-amplify";
import Axios from "axios";
import { API_ROUTES } from 'src/config/api';
import { Button, Spinner, Card, CardBody, CardFooter } from "reactstrap";
import { brandLogoTransformer } from "src/transformer/transaction-type";
import CountryTypeTransformer from "src/transformer/country-type-transformer";
import { useLocation } from 'react-router-dom';

const UserFeatures = () => {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [directDebitSetup, setDirectDebitSetup] = useState(false);
  const [rates, setRates] = useState([]);
  const [reload, setReload] = useState(0);

  const { organisation } = useSelector((state) => state);

  const params = useLocation();
  const query = new URLSearchParams(params.search);
  const ddFound = query.get('in_progress') ? true : false;

  const createDirectDebit = async (e) => {
    e.preventDefault();

    const user = await Auth.currentAuthenticatedUser();

    const response = await Axios(
      API_ROUTES.bank['direct-debit:create'](organisation.organisation),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    window.location.href = response.data.data.url;
  }

  const getPayoutConfiguration = useCallback(async () => {
    if (organisation?.organisation) {
      setLoading(true);
      const user = await Auth.currentAuthenticatedUser();

      const result = await Axios(
        API_ROUTES.user['organisation:surcharging:get'](organisation.organisation),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      setLoading(false)
      setEnabled(result.data.data.enabled);
      setDirectDebitSetup(result.data.data.direct_debit);
      setRates(result.data.data.rates)
    }
  }, [organisation, reload]);

  const toggleChange = async () => {
    setLoading(true);

    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.user['organisation:surcharging:toggle'](organisation.organisation),
      {
        method: 'post',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    setReload(reload + 1);
  }

  useEffect(() => {
    getPayoutConfiguration();
  }, [getPayoutConfiguration]);

  if (loading) return (
    <div>
      <Head title="Organisation Surcharging" />
      <Block>
        <div className="mt-3">
          <Skeleton count={1} height="130px" className="mb-4" style={{
            width: '100%',
          }} />
        </div>
        <div className="mt-3">
          <Skeleton count={5} height="50px" className="mb-4" style={{
            width: '100%',
          }} />
        </div>
      </Block>
    </div>
  );

  if (directDebitSetup === false && ddFound) return (
    <div>
      <Head title="Organisation Surcharging" />
      <Block>
        <div className="border bg-green-50 border border-green-300 rounded-md">
          <div className="text-white overflow-hidden p-4">
            <div className="text-green-400 text-lg font-semibold">
              <p className="text-md text-green-700"><strong>Your direct debit mandate has been received!</strong></p>
              <p className="text-sm text-green-700">A member of our team has been alerted. It will take up to an hour for us to receive confirmation of the mandate, please check back later.</p>
            </div>
          </div>
        </div>
      </Block>
    </div>
  );

  if (directDebitSetup === false) return (
    <div>
      <Head title="Surcharging Configration" />
      <div className="items-center pt-10 pb-12 px-8 grid md:grid-cols-2 w-full border border-violet-300 rounded-lg bg-violet-50">

       <Card className="pr-4 bg-violet-50">
          <div>
            <div>
              <h3 className="text-lg font-bold mb-3 text-violet-800">Surcharging Benefits</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>Recover payment processing costs</li>
                <li>Transparent fee structure for customers</li>
                <li>Automatic calculation based on transaction amount</li>
                <li>Detailed reporting and analytics</li>
              </ul>
            </div>
            <div className="mt-4">
              <h3 className="text-lg font-bold mb-3 text-violet-800">How It Works</h3>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Set up your direct debit mandate</li>
                <li>Enable Surcharging</li>
                <li>We automatically Apply surcharges automatically to transactions</li>
                <li>You Receive the full transaction amount plus surcharge</li>
                <li>Felloh invoices 10% of the surcharge amount on a monthly basis</li>
              </ol>
            </div>
          </div>
        </Card>

        <Card className="max-w-2xl border border-violet-900">
          <CardBody className="space-y-4 p-8">
            <p className="text-violet-800 font-bold">
              A direct debit is required in order to enable Surcharging!
            </p>
            <p className="text-gray-600">
              Felloh charges <strong>10%</strong> of the surcharge amount per transaction for this facility, these charges will be invoiced and undertaken via direct debit on a month end basis.
            </p>
            <div class="flex-grow border-t border-gray-400" />
            <p className="text-violet-800 text-sm">
              <strong>Example: </strong>On a £1,000 transaction with a 2% surcharge, You would receive an extra £20.00 and Felloh will invoice you £2.00.
            </p>
          </CardBody>
          <CardFooter className="flex justify-center">
            <Button color="primary" className="bg-violet-800" size="md" disabled={loading} onClick={createDirectDebit}>
              <span>
                {loading === true ? <Spinner size="sm" className="mt-1 mr-3"> </Spinner> : 'Agree & Setup Direct Debit'}</span>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );

  return (
    <div>
      <Head title="Organisation Surcharging" />
      <Block>

        <Card className="flex flex-row w-full border border-violet-200 rounded-lg p-4 bg-violet-50">
          <div className="basis-3/4 text-violet-800">
            <p className="text-violet-800 text-lg font-semibold">
              Surcharging is <strong>{enabled === true ? 'Enabled' : 'Disabled'}</strong> <span className="text-sm text-slate-900 font-normal">(Toggle the switch to enable or disable surcharging)</span>
            </p>
            <p className="text-violet-900 text-xs mt-[4px]">
              Felloh charges <strong>10%</strong> of the surcharge amount per transaction for this facility, these charges will be invoiced and undertaken via direct debit on a month end basis.
            </p>
          </div>
          <div className="basis-1/4 pt-2">
            <div className="float-right">
              <span>{loading && <Spinner size="sm" className="mt-1 mr-3"> </Spinner>}</span>
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input form-control"
                  checked={enabled}
                  disabled={loading}
                  onChange={() => toggleChange()}
                  id={`payouts-switch`}
                />
                <label className="custom-control-label" htmlFor={`payouts-switch`} />
              </div>
            </div>

          </div>
        </Card>

        <div className="py-6 px-4 border border-violet-200 rounded-lg mt-4">
          <div className="border-b border-violet-800 pb-2 mb-4">
            <h2 className="text-violet-800 text-xl font-semibold">Applicable Surcharging Rates</h2>
            <p className="text-xs text-slate-600 my-1">* Surcharges can only be applied to international & corporate cards. Surcharges are based on your acquiring rates (inline with scheme rules).</p>
          </div>

          <table className="table-auto w-full">
            <thead>
              <tr>
                <th scope="col" className="pb-2 text-start text-xs font-medium text-gray-500 uppercase">Card Type</th>
                <th scope="col" colSpan={2} className="pb-2 text-start text-xs font-medium text-gray-500 uppercase text-left">Scheme</th>
                <th scope="col" className="pb-2 text-start text-xs font-medium text-gray-500 uppercase">Level</th>
                <th scope="col" className="pb-2 text-start text-xs font-medium text-gray-500 uppercase">Region</th>
                <th scope="col" className="pb-2 text-start text-xs font-medium text-gray-500 uppercase">Surcharge</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rates.map((rate, index) => {
                const transactionType = brandLogoTransformer(rate.scheme);
                const countryType = CountryTypeTransformer(rate.countryType);

                return (
                  <tr key={index}>
                    <td className="py-2 whitespace-nowrap text-sm font-medium text-gray-800">{rate.type}</td>
                    <td className="text-center border-r align-middle w-[45px]">
                      <div style={{ width: '38px', padding: '0 4px 0 6px', maxWidth: '38px', margin: '0 auto' }}>
                        {
                          typeof transactionType.image !== 'undefined' && (
                            <img src={transactionType.image} className={`${transactionType.imageClass} -mt-0.5`} />
                          )
                        }
                        {
                          typeof transactionType.image === 'undefined' && (
                            <Icon name={`ni text-primary icon ${transactionType.icon}`} style={{ fontSize: '21px' }} />
                          )
                        }
                      </div>
                    </td>
                    <td className="py-2 whitespace-nowrap text-sm text-gray-800">{transactionType.name}</td>
                    <td className="py-2 whitespace-nowrap text-sm text-gray-800">{rate.level}</td>
                    <td className="py-2 whitespace-nowrap text-sm text-gray-800">{countryType}</td>
                    <td className="py-2 whitespace-nowrap text-sm text-gray-800">{rate.cost}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </Block>
    </div>
  );
};
export default UserFeatures;
